import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const CURRENT_YEAR = new Date().getFullYear();
const PERIODS = [1, 2, 3, 4];

function groupByStudentSession(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const key = `${r.crsuid}|${r.satdate}|${r.class_no}|${r.studid}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        satdate: r.satdate,
        class_no: r.class_no,
        studid: r.studid,
        student_name: r.student_name,
        subject: r.subject,
        chapter: r.chapter,
        unitno: r.unitno,
        periodValues: {},
        attstatus: r.attstatus,
        reason: r.reason,
        crdtime: 0,
      });
    }
    const g = map.get(key);
    g.periodValues[r.period] = r.crdtime;
    g.crdtime += Number(r.crdtime) || 0;
    // 출석이 아닌(조퇴 등) 기록이 있으면 그것을 대표 상태로 표시
    if (g.attstatus === '출석' && r.attstatus !== '출석') {
      g.attstatus = r.attstatus;
      g.reason = r.reason;
    }
  });
  return Array.from(map.values());
}

export default function AttendanceSearch() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';

  const [grade, setGrade] = useState(1);
  const [classtype, setClasstype] = useState('R');
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [satdate, setSatdate] = useState('');
  const [excludePresent, setExcludePresent] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [teachid, setTeachid] = useState('');
  const [rawResults, setRawResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const year = useMemo(() => (satdate ? Number(satdate.slice(0, 4)) : CURRENT_YEAR), [satdate]);

  useEffect(() => {
    if (isAdmin) {
      api.get('/admin/teachers').then((res) => setTeachers(res.data));
    }
  }, [isAdmin]);

  useEffect(() => {
    setClassNo('');
    api.get('/classes', { params: { grade, classtype } }).then((res) => setClassOptions(res.data));
  }, [grade, classtype]);

  useEffect(() => {
    setLoading(true);
    api
      .get('/attendance/search', {
        params: {
          year,
          grade,
          classtype,
          class_no: classNo === '' ? undefined : classNo,
          satdate: satdate === '' ? undefined : satdate,
          exclude_present: excludePresent,
          teachid: isAdmin && teachid ? teachid : undefined,
        },
      })
      .then((res) => setRawResults(res.data))
      .finally(() => setLoading(false));
  }, [year, grade, classtype, classNo, satdate, excludePresent, teachid, isAdmin]);

  const rows = useMemo(() => groupByStudentSession(rawResults), [rawResults]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>출결조회</h2>
      </div>

      <div className="card">
        <div className="form-row">
          {isAdmin && (
            <div className="form-field form-field-narrow">
              <label>담당교사</label>
              <select value={teachid} onChange={(e) => setTeachid(e.target.value)}>
                <option value="">전체</option>
                {teachers.map((t) => (
                  <option key={t.teachid} value={t.teachid}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-field form-field-narrow">
            <label>학년</label>
            <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>구분</label>
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>반</label>
            <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
              <option value="">전체</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}반</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>날짜</label>
            <input type="date" value={satdate} onChange={(e) => setSatdate(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <label className="section-check">
            <input
              type="checkbox"
              checked={excludePresent}
              onChange={(e) => setExcludePresent(e.target.checked)}
            />
            출석이 아닌 학생만 보기
          </label>
        </div>

        <div className="table-scroll">
          <table className="data-table attendance-table">
            <thead>
              <tr>
                <th>날짜</th>
                <th>반</th>
                <th>학번</th>
                <th>이름</th>
                <th>과목</th>
                <th>교육내용</th>
                <th>차수</th>
                {PERIODS.map((p) => (
                  <th key={p}>{p}교시</th>
                ))}
                <th>출결상태</th>
                <th>사유</th>
                <th>인정시수</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.satdate}</td>
                  <td>{r.class_no}반</td>
                  <td>{r.studid}</td>
                  <td>{r.student_name}</td>
                  <td>{r.subject}</td>
                  <td>{r.chapter}</td>
                  <td>{r.unitno}</td>
                  {PERIODS.map((p) => (
                    <td key={p}>{r.periodValues[p] ?? ''}</td>
                  ))}
                  <td>{r.attstatus}</td>
                  <td>{r.reason}</td>
                  <td className="credit-total-cell">{r.crdtime}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={PERIODS.length + 10} className="empty-row">조건에 맞는 출결 기록이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
